import type { JSX } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { Avatar } from './ui.js';
import { IconFriends, IconGift, IconGiving, IconLists, IconProfile } from './icons.js';

/**
 * Каркас приложения.
 *
 * На телефоне навигация внизу (большим пальцем до неё дотянуться легко),
 * на широком экране — в шапке. Это два представления одного и того же набора
 * разделов, а не разные меню: человек, пересевший с телефона за компьютер,
 * должен видеть привычные пункты.
 */

interface NavItem {
  to: string;
  label: string;
  Icon: (props: { size?: number }) => JSX.Element;
  /** Совпадение только по точному адресу — иначе «/» подсвечивается везде. */
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Мои списки', Icon: IconLists, end: true },
  { to: '/feed', label: 'Друзьям', Icon: IconGift },
  { to: '/gifts', label: 'Я дарю', Icon: IconGiving },
  { to: '/friends', label: 'Друзья', Icon: IconFriends },
  { to: '/profile', label: 'Профиль', Icon: IconProfile },
];

export function Layout(): JSX.Element {
  const { user } = useAuth();
  const { pathname } = useLocation();

  return (
    <div className="app-shell">
      <header className="topbar">
        <NavLink to="/" className="topbar__brand">
          <span className="topbar__mark" aria-hidden="true">
            ✦
          </span>
          Загадарь
        </NavLink>

        <nav className="topbar__links" aria-label="Основная навигация">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `topbar__link ${isActive ? 'topbar__link--active' : ''}`.trim()}
            >
              {item.label}
              {item.to === '/friends' && (user?.pendingFriendRequests ?? 0) > 0 && (
                <span
                  aria-label={`${user?.pendingFriendRequests} новых заявок`}
                  style={{
                    display: 'inline-grid',
                    placeItems: 'center',
                    minWidth: 18,
                    height: 18,
                    marginLeft: 6,
                    padding: '0 5px',
                    borderRadius: 999,
                    background: 'var(--danger)',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                  }}
                >
                  {user?.pendingFriendRequests}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <NavLink to="/profile" className="topbar__me" aria-label="Профиль">
          <Avatar name={user?.displayName ?? '?'} src={user?.avatarUrl} size="sm" />
        </NavLink>
      </header>

      {/*
        Ключ по адресу нужен ради появления страницы.

        Появление задано анимацией, а анимация в css проигрывается один раз —
        на появлении элемента. Без ключа main живёт от первой загрузки до
        закрытия вкладки, и переход между разделами проходил бы совсем без
        отклика: содержимое подменяется мгновенно, и человек не понимает,
        сменилась страница или просто дрогнула. Ключ заставляет react собрать
        main заново, и анимация играет на каждом переходе.

        Своё состояние страницы это не роняет: при смене адреса react-router
        и так рисует другой компонент, а при возврате на тот же адрес ключ не
        меняется и пересборки не происходит.
      */}
      <main className="app-main" key={pathname}>
        <Outlet />
      </main>

      <nav className="bottomnav" aria-label="Навигация">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `bottomnav__item ${isActive ? 'bottomnav__item--active' : ''}`.trim()}
          >
            <span className="bottomnav__icon">
              <item.Icon />
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
